import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, BookOpenText, Newspaper, CalendarDots, Confetti, Users, ProhibitInset, Globe, Buildings, ImageSquare, UploadSimple, Lock, UserPlus, X, Gift, Flag, Stack, House } from "@phosphor-icons/react";
import ImageUploadField from "@/components/ImageUploadField";

const MONTHS = [
  { v: 1, l: "Janeiro" }, { v: 2, l: "Fevereiro" }, { v: 3, l: "Março" },
  { v: 4, l: "Abril" }, { v: 5, l: "Maio" }, { v: 6, l: "Junho" },
  { v: 7, l: "Julho" }, { v: 8, l: "Agosto" }, { v: 9, l: "Setembro" },
  { v: 10, l: "Outubro" }, { v: 11, l: "Novembro" }, { v: 12, l: "Dezembro" },
];

function emptyManga() {
  return { title: "", slug: "", author: "", publisher: "", genres: "", synopsis: "", cover_url: "", youtube_url: "", purchase_links: "", release_year: "", release_month: "", isbn: "", pages: "", series_name: "", series_total: "", volume_number: "", status: "", publish_at: "" };
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function mangaToForm(m) {
  return {
    title: m.title || "",
    slug: m.slug || "",
    author: m.author || "",
    publisher: m.publisher || "",
    genres: (m.genres || []).join(", "),
    synopsis: m.synopsis || "",
    cover_url: m.cover_url || "",
    youtube_url: m.youtube_url || "",
    purchase_links: (m.purchase_links || []).map(p => `${p.store}|${p.url}`).join("\n"),
    release_year: m.release_year || "",
    release_month: m.release_month || "",
    isbn: m.isbn || "",
    pages: m.pages || "",
    series_name: m.series_name || "",
    series_total: m.series_total || "",
    volume_number: m.volume_number || "",
    status: m.status || "",
    publish_at: isoToLocalInput(m.publish_at),
  };
}

function slugify(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function formToPayload(form) {
  return {
    title: form.title,
    slug: slugify(form.slug),
    author: form.author,
    publisher: form.publisher,
    genres: form.genres.split(",").map(g => g.trim()).filter(Boolean),
    synopsis: form.synopsis,
    cover_url: form.cover_url,
    youtube_url: form.youtube_url,
    isbn: form.isbn,
    pages: form.pages ? parseInt(form.pages) : null,
    purchase_links: form.purchase_links.split("\n").map(l => {
      const [store, ...rest] = l.split("|");
      return store && rest.length ? { store: store.trim(), url: rest.join("|").trim() } : null;
    }).filter(Boolean),
    release_year: form.release_year ? parseInt(form.release_year) : null,
    release_month: form.release_month ? parseInt(form.release_month) : null,
    series_name: form.series_name || "",
    series_total: form.series_total ? parseInt(form.series_total) : null,
    volume_number: form.volume_number ? parseInt(form.volume_number) : null,
    status: form.status || "",
    publish_at: localInputToIso(form.publish_at),
  };
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (user.role !== "admin") { toast.error("Acesso restrito"); navigate("/"); }
    }
  }, [user, loading, navigate]);

  if (!user || user.role !== "admin") return null;

  const initialTab = new URLSearchParams(window.location.search).get("tab") || "painel";

  const SECTIONS = [
    { key: "painel", label: "Painel", icon: House, comp: <PainelDashboard /> },
    { key: "manga", label: "Mangás", icon: BookOpenText, comp: <MangaAdmin /> },
    { key: "series", label: "Séries", icon: Stack, comp: <SeriesAdmin /> },
    { key: "news", label: "Notícias", icon: Newspaper, comp: <NewsAdmin /> },
    { key: "releases", label: "Lançamentos", icon: CalendarDots, comp: <ReleasesAdmin /> },
    { key: "events", label: "Eventos", icon: Confetti, comp: <EventsAdmin /> },
    { key: "publishers", label: "Editoras", icon: Buildings, comp: <PublishersAdmin /> },
    { key: "banners", label: "Banners", icon: ImageSquare, comp: <BannersAdmin /> },
    { key: "home-banners", label: "Home", icon: House, comp: <HomeBannersAdmin /> },
    { key: "giveaways", label: "Sorteios", icon: Gift, comp: <GiveawaysAdmin /> },
    { key: "members", label: "Membros", icon: Users, comp: <MembersAdmin /> },
    { key: "moderation", label: "Moderação", icon: Flag, comp: <ModerationAdmin /> },
  ];

  return <AdminShell sections={SECTIONS} initial={initialTab} />;
}

function AdminShell({ sections, initial }) {
  const valid = sections.some(s => s.key === initial) ? initial : "painel";
  const [active, setActive] = useState(valid);
  const current = sections.find(s => s.key === active) || sections[0];

  return (
    <div className="pt-20 max-w-[1400px] mx-auto px-4 lg:px-8 pb-24" data-testid="admin-page">
      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="font-mono-accent text-xs text-[#E63946] mb-3 px-2">◢ ADMIN</div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="admin-sidebar">
            {sections.map(s => (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                data-testid={`admin-nav-${s.key}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-display font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${active === s.key ? "bg-[#E63946] text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
              >
                <s.icon size={18} weight={active === s.key ? "fill" : "bold"} /> {s.label}
              </button>
            ))}
          </nav>
          <div className="hidden lg:block mt-4 rounded-xl border border-white/5 bg-[#0f0f14] p-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-display font-semibold text-emerald-400">Sistema Online</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">Tudo a funcionar normalmente</div>
          </div>
        </aside>

        {/* Content */}
        <div className="min-w-0">
          <div className="mb-6">
            <div className="font-mono-accent text-xs text-[#E63946] mb-2">◢ ADMIN</div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Painel de Administração</h1>
            <p className="text-zinc-400 mt-1 text-sm">Gerencie todo o conteúdo do site de forma simples e eficiente.</p>
          </div>
          <div key={current.key}>{current.comp}</div>
        </div>
      </div>
    </div>
  );
}

function PainelDashboard() {
  const [visits, setVisits] = useState(null);
  const [dash, setDash] = useState(null);
  useEffect(() => {
    api.get("/admin/stats/visits").then(r => setVisits(r.data)).catch(() => {});
    api.get("/stats/dashboard").then(r => setDash(r.data)).catch(() => {});
  }, []);

  const visitCards = [
    { key: "today", label: "Hoje", color: "#E63946" },
    { key: "week", label: "7 dias", color: "#7209B7" },
    { key: "month", label: "1 mês", color: "#F59E0B" },
    { key: "year", label: "1 ano", color: "#3B82F6" },
    { key: "all", label: "Desde sempre", color: "#10B981" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-painel">
      <section>
        <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Users size={20} weight="fill" className="text-[#E63946]" /> Visitantes do site</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="visit-stats">
          {visitCards.map(c => (
            <div key={c.key} className="rounded-2xl border border-white/5 bg-[#0f0f14] p-4" data-testid={`visit-${c.key}`}>
              <div className="font-mono-accent text-[10px] text-zinc-500 uppercase tracking-wider">{c.label}</div>
              <div className="font-display text-3xl font-extrabold mt-1" style={{ color: c.color }}>{visits ? (visits[c.key] ?? 0).toLocaleString("pt-PT") : "—"}</div>
              <div className="text-[10px] text-zinc-500 mt-1">visitantes únicos</div>
            </div>
          ))}
        </div>
      </section>

      {dash && (
        <section>
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><BookOpenText size={20} weight="fill" className="text-[#7209B7]" /> Conteúdo</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ContentStat label="Mangás" value={dash.manga?.total} weekly={dash.manga?.weekly} />
            <ContentStat label="Notícias" value={dash.news?.total} weekly={dash.news?.weekly} />
            <ContentStat label="Tópicos" value={dash.topics?.total} weekly={dash.topics?.weekly} />
            <ContentStat label="Membros" value={dash.members?.total} weekly={dash.members?.weekly} />
          </div>
        </section>
      )}
    </div>
  );
}

function ContentStat({ label, value, weekly }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-4">
      <div className="font-mono-accent text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-1">{value?.toLocaleString("pt-PT") ?? "—"}</div>
      {weekly !== undefined && <div className="text-[10px] text-emerald-400 mt-1">+{weekly} esta semana</div>}
    </div>
  );
}

function SeriesAdmin() {
  const [items, setItems] = useState([]);
  const [mangas, setMangas] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", description: "", image_url: "" });
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null); // series object
  const [selected, setSelected] = useState({});
  const [mSearch, setMSearch] = useState("");

  async function load() {
    const [s, m] = await Promise.all([api.get("/series"), api.get("/manga")]);
    setItems(s.data); setMangas(m.data);
  }
  useEffect(() => { load(); }, []);

  function slugify(v) { return v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""); }

  async function submit(e) {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/series/${editing}`, form); toast.success("Série atualizada"); }
      else { await api.post("/admin/series", form); toast.success("Série criada"); }
      setForm({ name: "", slug: "", description: "", image_url: "" }); setEditing(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  function openEdit(s) { setEditing(s.series_id); setForm({ name: s.name, slug: s.slug, description: s.description || "", image_url: s.image_url || "" }); }

  async function remove(id) {
    if (!confirm("Apagar esta série? (os mangás não são apagados)")) return;
    await api.delete(`/admin/series/${id}`); toast.success("Série apagada"); load();
  }

  function openAssign(s) {
    setAssigning(s);
    const pre = {};
    mangas.forEach(m => { if ((m.series_name || "").toLowerCase() === s.name.toLowerCase()) pre[m.manga_id] = true; });
    setSelected(pre); setMSearch("");
  }

  async function saveAssign() {
    const ids = Object.keys(selected).filter(k => selected[k]);
    try {
      await api.post(`/admin/series/${assigning.series_id}/assign`, { manga_ids: ids });
      toast.success(`${ids.length} mangás associados`);
      setAssigning(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  const filteredMangas = mangas.filter(m => m.title.toLowerCase().includes(mSearch.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-[1fr,1fr] gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6" data-testid="admin-series-form">
        <h2 className="font-display text-xl font-bold mb-4">{editing ? "Editar Série" : "Nova Série"}</h2>
        <FormField label="Nome da série"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} placeholder="Ex: One Piece" data-testid="series-name-input" className="bg-[#1C1C22] border-white/10" required /></FormField>
        <FormField label="Link (slug)"><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="one-piece" data-testid="series-slug-input" className="bg-[#1C1C22] border-white/10" required /></FormField>
        <FormField label="Descrição"><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-[#1C1C22] border-white/10" /></FormField>
        <ImageUploadField label="Imagem (capa da série)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="series-image" hint="Recomendado: 600x900px (vertical)" />
        <div className="flex gap-2 mt-4">
          {editing && <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => { setEditing(null); setForm({ name: "", slug: "", description: "", image_url: "" }); }}>Cancelar</Button>}
          <Button type="submit" className="btn-crimson flex-1" data-testid="series-submit-btn"><Plus size={16} weight="bold" className="mr-2" /> {editing ? "Guardar" : "Criar série"}</Button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">Os mangás com o mesmo nome de série associam-se automaticamente. Podes também associá-los manualmente em cada série →</p>
      </form>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Séries ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(s => (
            <div key={s.series_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-series-row-${s.series_id}`}>
              <div className="w-12 h-16 bg-[#1C1C22] rounded overflow-hidden flex-shrink-0">{s.image_url && <img src={resolveImageUrl(s.image_url)} alt="" className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{s.name}</div>
                <div className="text-xs text-zinc-500">{s.volume_count} volumes</div>
              </div>
              <button onClick={() => openAssign(s)} className="text-xs px-2.5 py-1.5 rounded-lg bg-[#7209B7]/20 text-[#c07be0] hover:bg-[#7209B7]/30" data-testid={`admin-series-assign-${s.series_id}`}>Associar</button>
              <button onClick={() => openEdit(s)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-series-edit-${s.series_id}`}><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => remove(s.series_id)} className="text-zinc-500 hover:text-[#E63946] p-2" data-testid={`admin-series-delete-${s.series_id}`}><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!assigning} onOpenChange={(v) => !v && setAssigning(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="series-assign-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Associar mangás a "{assigning?.name}"</DialogTitle></DialogHeader>
          <Input value={mSearch} onChange={e => setMSearch(e.target.value)} placeholder="Procurar mangá..." className="bg-[#1C1C22] border-white/10 mt-2" data-testid="series-assign-search" />
          <div className="mt-2 overflow-y-auto flex-1 space-y-1">
            {filteredMangas.map(m => (
              <label key={m.manga_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer" data-testid={`series-assign-item-${m.manga_id}`}>
                <input type="checkbox" checked={!!selected[m.manga_id]} onChange={e => setSelected({ ...selected, [m.manga_id]: e.target.checked })} className="accent-[#E63946] w-4 h-4" />
                <div className="w-8 h-11 bg-[#1C1C22] rounded overflow-hidden flex-shrink-0">{m.cover_url && <img src={m.cover_url} alt="" className="w-full h-full object-cover" />}</div>
                <span className="text-sm truncate">{m.title}</span>
              </label>
            ))}
          </div>
          <Button onClick={saveAssign} className="btn-crimson mt-3" data-testid="series-assign-save">Guardar associações</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModerationAdmin() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status } });
      setReports(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  async function deleteContent(r) {
    if (!confirm("Apagar este conteúdo permanentemente?")) return;
    try { await api.post(`/admin/reports/${r.report_id}/delete-content`); toast.success("Conteúdo apagado"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function ignore(r) {
    try { await api.post(`/admin/reports/${r.report_id}/ignore`); toast.success("Report ignorado"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function banAuthor(r) {
    if (!r.author_id) return;
    const duration = window.prompt("Duração do banimento (1h, 1d, 3d, 1w, 1m, permanent):", "1w");
    if (!duration) return;
    try { await api.post(`/admin/members/${r.author_id}/ban`, { duration, reason: `Report: ${r.reason}` }); toast.success("Autor banido"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  return (
    <div className="space-y-4" data-testid="admin-moderation">
      <div className="flex items-center gap-2">
        {["pending", "resolved", "ignored", "all"].map(s => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-lg text-sm font-display transition-colors ${status === s ? "bg-[#E63946] text-white" : "bg-[#121217] border border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`mod-filter-${s}`}>
            {s === "pending" ? "Pendentes" : s === "resolved" ? "Resolvidos" : s === "ignored" ? "Ignorados" : "Todos"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-10 font-mono-accent text-xs">A CARREGAR...</div>
      ) : reports.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-16 text-center" data-testid="mod-empty">
          <Flag size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <div className="font-display font-bold text-lg">Sem reports {status === "pending" ? "pendentes" : ""}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r.report_id} className="bg-[#121217] border border-white/5 rounded-xl p-4" data-testid={`mod-report-${r.report_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="text-[10px] bg-white/10 text-zinc-300 px-2 py-0.5 rounded font-mono-accent uppercase">{r.entity_type === "comment" ? "Comentário" : "Tópico"}</span>
                <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded font-mono-accent">{r.reason}</span>
                {r.status !== "pending" && <span className="text-[10px] bg-zinc-700/40 text-zinc-400 px-2 py-0.5 rounded font-mono-accent uppercase">{r.status}</span>}
                <span className="text-[10px] text-zinc-500 ml-auto">{new Date(r.created_at).toLocaleString("pt-PT")}</span>
              </div>
              <div className="text-sm text-zinc-300 bg-black/30 rounded-lg p-3 mb-3 whitespace-pre-line break-words">{r.content_snapshot || "(sem conteúdo)"}</div>
              <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-500">
                <span>Autor: <span className="text-zinc-300">{r.author_name || "—"}</span></span>
                <span>· Reportado por: <span className="text-zinc-300">{r.reporter_name}</span></span>
                <a href={r.link} className="text-[#E63946] hover:underline ml-1" data-testid={`mod-view-${r.report_id}`}>Ver no site →</a>
              </div>
              {r.status === "pending" && (
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" className="btn-crimson text-xs" onClick={() => deleteContent(r)} data-testid={`mod-delete-${r.report_id}`}>
                    <Trash size={14} weight="bold" className="mr-1" /> Apagar conteúdo
                  </Button>
                  <Button size="sm" variant="outline" className="border-white/15 text-xs" onClick={() => ignore(r)} data-testid={`mod-ignore-${r.report_id}`}>Ignorar</Button>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" onClick={() => banAuthor(r)} data-testid={`mod-ban-${r.report_id}`}>
                    <ProhibitInset size={14} weight="bold" className="mr-1" /> Banir autor
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MangaForm({ form, setForm }) {
  const previewSlug = slugify(form.slug) || "—";
  return (
    <div className="space-y-3">
      <FormField label="Título *"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="manga-title-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Link personalizado * (URL)">
        <div className="flex items-center gap-1.5 rounded-md border border-white/10 bg-[#1C1C22] px-2 focus-within:border-[#E63946]/60">
          <span className="text-xs text-zinc-500 font-mono select-none whitespace-nowrap">portugalmanga.pt/manga/</span>
          <Input
            required
            value={form.slug}
            onChange={e => setForm({...form, slug: e.target.value})}
            onBlur={e => setForm({...form, slug: slugify(e.target.value)})}
            placeholder="one-piece-01"
            data-testid="manga-slug-input"
            className="bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-9 font-mono text-sm"
          />
        </div>
        <div className="mt-1 text-[11px] text-zinc-500 font-mono truncate" data-testid="manga-slug-preview">
          → portugalmanga.pt/manga/<span className="text-[#E63946]">{previewSlug}</span>
        </div>
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Autor"><Input value={form.author} onChange={e => setForm({...form, author: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
        <FormField label="Editora"><Input value={form.publisher} onChange={e => setForm({...form, publisher: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
      </div>
      <FormField label="Géneros (separados por vírgula)"><Input value={form.genres} onChange={e => setForm({...form, genres: e.target.value})} placeholder="Ação, Aventura, Shonen" className="bg-[#1C1C22] border-white/10" /></FormField>
      <ImageUploadField label="Capa do mangá" value={form.cover_url} onChange={(v) => setForm({...form, cover_url: v})} testid="manga-cover" hint="Recomendado: 600x900px (vertical)" />
      <div className="grid grid-cols-2 gap-3">
        <FormField label="ISBN"><Input value={form.isbn} onChange={e => setForm({...form, isbn: e.target.value})} placeholder="978-989-..." data-testid="manga-isbn-input" className="bg-[#1C1C22] border-white/10" /></FormField>
        <FormField label="Páginas"><Input type="number" value={form.pages} onChange={e => setForm({...form, pages: e.target.value})} placeholder="192" data-testid="manga-pages-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      </div>
      <FormField label="YouTube URL (vídeo de apresentação)"><Input value={form.youtube_url} onChange={e => setForm({...form, youtube_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Onde comprar (uma por linha: Loja|URL)">
        <Textarea rows={3} value={form.purchase_links} onChange={e => setForm({...form, purchase_links: e.target.value})} placeholder="Bedeteca|https://bedeteca.pt&#10;Bertrand|https://bertrand.pt" className="bg-[#1C1C22] border-white/10 font-mono text-sm" />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Mês de Lançamento">
          <Select value={form.release_month ? String(form.release_month) : ""} onValueChange={v => setForm({...form, release_month: v})}>
            <SelectTrigger className="bg-[#1C1C22] border-white/10" data-testid="manga-month-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="bg-[#121217] border-white/10 text-white">
              {MONTHS.map(m => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Ano de Lançamento"><Input type="number" value={form.release_year} onChange={e => setForm({...form, release_year: e.target.value})} placeholder="2024" className="bg-[#1C1C22] border-white/10" /></FormField>
      </div>
      <FormField label="Sinopse"><Textarea rows={4} value={form.synopsis} onChange={e => setForm({...form, synopsis: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
      <div className="rounded-lg border border-white/10 bg-[#151519] p-3 space-y-3">
        <div className="font-mono-accent text-[10px] text-zinc-400">SÉRIE (opcional — para agrupar volumes)</div>
        <FormField label="Nome da série"><Input value={form.series_name} onChange={e => setForm({...form, series_name: e.target.value})} placeholder="Ex: One Piece (deixa vazio para detetar pelo título)" data-testid="manga-series-name-input" className="bg-[#1C1C22] border-white/10" /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Nº do volume"><Input type="number" value={form.volume_number} onChange={e => setForm({...form, volume_number: e.target.value})} placeholder="1" data-testid="manga-volume-input" className="bg-[#1C1C22] border-white/10" /></FormField>
          <FormField label="Total de volumes"><Input type="number" value={form.series_total} onChange={e => setForm({...form, series_total: e.target.value})} placeholder="24" data-testid="manga-series-total-input" className="bg-[#1C1C22] border-white/10" /></FormField>
        </div>
        <FormField label="Estado da publicação">
          <Select value={form.status || "none"} onValueChange={v => setForm({...form, status: v === "none" ? "" : v})}>
            <SelectTrigger className="bg-[#1C1C22] border-white/10" data-testid="manga-status-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="bg-[#121217] border-white/10 text-white">
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="Em curso">Em curso</SelectItem>
              <SelectItem value="Concluído">Concluído</SelectItem>
              <SelectItem value="Em hiato">Em hiato</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <div className="rounded-lg border border-[#E63946]/25 bg-[#E63946]/[0.06] p-3 space-y-2">
        <div className="font-mono-accent text-[10px] text-[#E63946]">AGENDAR PUBLICAÇÃO (opcional)</div>
        <FormField label="Data e hora de lançamento">
          <Input
            type="datetime-local"
            value={form.publish_at}
            onChange={e => setForm({...form, publish_at: e.target.value})}
            data-testid="manga-publish-at-input"
            className="bg-[#1C1C22] border-white/10 [color-scheme:dark]"
          />
        </FormField>
        <div className="text-[11px] text-zinc-400 leading-relaxed">
          {form.publish_at
            ? `Este mangá só ficará visível para os utilizadores a partir de ${new Date(form.publish_at).toLocaleString("pt-PT")}. Até lá, só os administradores conseguem vê-lo e adicioná-lo às listas.`
            : "Deixa vazio para publicar imediatamente. Se definires uma data, a página fica bloqueada para os utilizadores até essa data/hora."}
          {form.publish_at && (
            <button type="button" onClick={() => setForm({...form, publish_at: ""})} data-testid="manga-publish-clear" className="ml-2 text-[#E63946] hover:underline">Limpar</button>
          )}
        </div>
      </div>
    </div>
  );
}

function MangaAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyManga());
  const [editing, setEditing] = useState(null); // {manga_id, form}
  const [editForm, setEditForm] = useState(emptyManga());
  const [searchParams, setSearchParams] = useSearchParams();

  async function load() { const { data } = await api.get("/manga"); setItems(data); return data; }
  useEffect(() => {
    (async () => {
      const data = await load();
      const editId = searchParams.get("edit");
      if (editId) {
        const m = data.find(x => x.manga_id === editId || x.slug === editId);
        if (m) { setEditing(m.manga_id); setEditForm(mangaToForm(m)); }
        const sp = new URLSearchParams(searchParams);
        sp.delete("edit");
        setSearchParams(sp, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/manga", formToPayload(form));
      toast.success("Mangá adicionado");
      setForm(emptyManga());
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/manga/${editing}`, formToPayload(editForm));
      toast.success("Mangá atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  function openEdit(m) {
    setEditing(m.manga_id);
    setEditForm(mangaToForm(m));
  }

  async function remove(id) {
    if (!confirm("Apagar?")) return;
    await api.delete(`/manga/${id}`);
    toast.success("Apagado");
    load();
  }

  return (
    <div className="grid lg:grid-cols-[1fr,1fr] gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6" data-testid="admin-manga-form">
        <h2 className="font-display text-xl font-bold mb-4">Novo Mangá</h2>
        <MangaForm form={form} setForm={setForm} />
        <Button type="submit" className="btn-crimson w-full mt-4" data-testid="manga-submit-btn"><Plus size={16} weight="bold" className="mr-2" /> Adicionar</Button>
      </form>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Mangás existentes ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(m => (
            <div key={m.manga_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-manga-row-${m.manga_id}`}>
              <div className="w-12 h-16 bg-[#1C1C22] rounded overflow-hidden flex-shrink-0">
                {m.cover_url && <img src={m.cover_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate flex items-center gap-2">
                  {m.title}
                  {m.publish_at && new Date(m.publish_at) > new Date() && (
                    <span className="text-[9px] uppercase font-mono-accent tracking-wide bg-[#E63946]/20 text-[#E63946] px-1.5 py-0.5 rounded" title={`Publica em ${new Date(m.publish_at).toLocaleString("pt-PT")}`} data-testid={`admin-manga-scheduled-${m.manga_id}`}>Agendado</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500">
                  {m.publish_at && new Date(m.publish_at) > new Date()
                    ? `Publica ${new Date(m.publish_at).toLocaleString("pt-PT")}`
                    : `${m.author || "—"} ${m.isbn ? `· ISBN ${m.isbn}` : ""}`}
                </div>
              </div>
              <button onClick={() => openEdit(m)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-manga-edit-${m.manga_id}`} title="Editar"><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => remove(m.manga_id)} className="text-zinc-500 hover:text-[#E63946] p-2" data-testid={`admin-manga-delete-${m.manga_id}`} title="Apagar"><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Editar Mangá</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="mt-2">
            <MangaForm form={editForm} setForm={setEditForm} />
            <div className="mt-4 flex gap-2">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson" data-testid="manga-edit-save-btn">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewsAdmin() {
  const [items, setItems] = useState([]);
  const empty = { title: "", summary: "", content: "", image_url: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);

  async function load() { const { data } = await api.get("/news"); setItems(data); }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/news", form);
      toast.success("Notícia publicada");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/news/${editing}`, editForm);
      toast.success("Notícia atualizada");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function remove(id) { if (!confirm("Apagar?")) return; await api.delete(`/news/${id}`); load(); }
  function openEdit(n) { setEditing(n.news_id); setEditForm({ title: n.title || "", summary: n.summary || "", content: n.content || "", image_url: n.image_url || "" }); }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Nova Notícia</h2>
        <NewsFormFields form={form} setForm={setForm} />
        <Button type="submit" className="btn-crimson w-full" data-testid="news-submit-btn">Publicar</Button>
      </form>
      <div>
        <h2 className="font-display text-xl font-bold mb-3">Notícias ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(n => (
            <div key={n.news_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{n.title}</div>
                <div className="text-xs text-zinc-500">{new Date(n.created_at).toLocaleDateString("pt-PT")}</div>
              </div>
              <button onClick={() => openEdit(n)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-news-edit-${n.news_id}`} title="Editar"><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => remove(n.news_id)} className="text-zinc-500 hover:text-[#E63946] p-2" data-testid={`admin-news-delete-${n.news_id}`}><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Notícia</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <NewsFormFields form={editForm} setForm={setEditForm} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson" data-testid="news-edit-save-btn">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewsFormFields({ form, setForm }) {
  return (
    <>
      <FormField label="Título *"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="news-title-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <ImageUploadField label="Imagem" value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} testid="admin-image" />
      <FormField label="Resumo"><Textarea rows={2} value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Conteúdo (HTML suportado)">
        <Textarea rows={8} value={form.content} onChange={e => setForm({...form, content: e.target.value})} data-testid="news-content-input" className="bg-[#1C1C22] border-white/10 font-mono text-sm" placeholder='Podes usar HTML e embeds. Ex:&#10;<p>Parágrafo</p>&#10;<img src="https://..." />&#10;<iframe src="https://www.youtube.com/embed/VIDEO_ID" allowfullscreen></iframe>' />
        <div className="text-[10px] text-zinc-500 mt-1">
          Suporta HTML, iframes (YouTube, Twitter), imagens e links. Parágrafos simples também funcionam.
        </div>
      </FormField>
    </>
  );
}

function ReleasesAdmin() {
  const [items, setItems] = useState([]);
  const empty = { title: "", cover_url: "", publisher: "", release_date: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);

  async function load() { const { data } = await api.get("/releases"); setItems(data); }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/releases", form);
      toast.success("Lançamento adicionado");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/releases/${editing}`, editForm);
      toast.success("Lançamento atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function remove(id) { if (!confirm("Apagar?")) return; await api.delete(`/releases/${id}`); load(); }
  function openEdit(r) { setEditing(r.release_id); setEditForm({ title: r.title || "", cover_url: r.cover_url || "", publisher: r.publisher || "", release_date: r.release_date || "" }); }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Novo Lançamento</h2>
        <ReleaseFormFields form={form} setForm={setForm} />
        <Button type="submit" className="btn-crimson w-full" data-testid="release-submit-btn">Adicionar</Button>
      </form>
      <div>
        <h2 className="font-display text-xl font-bold mb-3">Lançamentos ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(r => (
            <div key={r.release_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{r.title}</div>
                <div className="text-xs text-zinc-500">{new Date(r.release_date).toLocaleDateString("pt-PT")} · {r.publisher}</div>
              </div>
              <button onClick={() => openEdit(r)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-release-edit-${r.release_id}`}><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => remove(r.release_id)} className="text-zinc-500 hover:text-[#E63946] p-2"><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Lançamento</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <ReleaseFormFields form={editForm} setForm={setEditForm} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReleaseFormFields({ form, setForm }) {
  return (
    <>
      <FormField label="Título *"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="release-title-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Editora"><Input value={form.publisher} onChange={e => setForm({...form, publisher: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
      <ImageUploadField label="Capa" value={form.cover_url} onChange={(v) => setForm({...form, cover_url: v})} testid="release-cover" hint="Recomendado: 600x900px (vertical)" />
      <FormField label="Data de Lançamento *"><Input required type="date" value={form.release_date} onChange={e => setForm({...form, release_date: e.target.value})} data-testid="release-date-input" className="bg-[#1C1C22] border-white/10" /></FormField>
    </>
  );
}

function EventsAdmin() {
  const [items, setItems] = useState([]);
  const empty = { title: "", description: "", location: "", event_date: "", image_url: "", url: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);

  async function load() { const { data } = await api.get("/events"); setItems(data); }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/events", form);
      toast.success("Evento adicionado");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/events/${editing}`, editForm);
      toast.success("Evento atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function remove(id) { if (!confirm("Apagar?")) return; await api.delete(`/events/${id}`); load(); }
  function openEdit(ev) {
    setEditing(ev.event_id);
    setEditForm({
      title: ev.title || "", description: ev.description || "", location: ev.location || "",
      event_date: ev.event_date || "", image_url: ev.image_url || "", url: ev.url || ""
    });
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Novo Evento</h2>
        <EventFormFields form={form} setForm={setForm} />
        <Button type="submit" className="btn-crimson w-full" data-testid="event-submit-btn">Adicionar</Button>
      </form>
      <div>
        <h2 className="font-display text-xl font-bold mb-3">Eventos ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(e => (
            <div key={e.event_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{e.title}</div>
                <div className="text-xs text-zinc-500">{new Date(e.event_date).toLocaleDateString("pt-PT")} {e.location ? `· ${e.location}` : ""}</div>
              </div>
              <button onClick={() => openEdit(e)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-event-edit-${e.event_id}`}><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => remove(e.event_id)} className="text-zinc-500 hover:text-[#E63946] p-2"><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Evento</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <EventFormFields form={editForm} setForm={setEditForm} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventFormFields({ form, setForm }) {
  return (
    <>
      <FormField label="Título *"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Local"><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Data *"><Input required type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} data-testid="event-date-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <ImageUploadField label="Imagem" value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} testid="admin-image" />
      <FormField label="Link externo"><Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Descrição"><Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#1C1C22] border-white/10" /></FormField>
    </>
  );
}

const BAN_DURATIONS = [
  { v: "1h", l: "1 hora" },
  { v: "1d", l: "1 dia" },
  { v: "3d", l: "3 dias" },
  { v: "1w", l: "1 semana" },
  { v: "1m", l: "1 mês" },
  { v: "permanent", l: "Permanente" },
];

function PublishersAdmin() {
  const [items, setItems] = useState([]);
  const empty = { name: "", website: "", description: "", image_url: "", founded_year: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);

  async function load() { const { data } = await api.get("/publishers"); setItems(data); }
  useEffect(() => { load(); }, []);

  function toPayload(f) {
    return { ...f, founded_year: f.founded_year ? parseInt(f.founded_year) : null };
  }

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/publishers", toPayload(form));
      toast.success("Editora adicionada");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/publishers/${editing}`, toPayload(editForm));
      toast.success("Editora atualizada");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function remove(id) { if (!confirm("Apagar editora?")) return; await api.delete(`/publishers/${id}`); load(); }
  function openEdit(p) {
    setEditing(p.publisher_id);
    setEditForm({
      name: p.name || "", website: p.website || "", description: p.description || "",
      image_url: p.image_url || "", founded_year: p.founded_year || ""
    });
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Nova Editora</h2>
        <PublisherFormFields form={form} setForm={setForm} />
        <Button type="submit" className="btn-crimson w-full" data-testid="publisher-submit-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Adicionar
        </Button>
      </form>
      <div>
        <h2 className="font-display text-xl font-bold mb-3">Editoras ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(p => (
            <div key={p.publisher_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3">
              {p.image_url ? (
                <img src={p.image_url} alt="" className="w-12 h-12 object-contain bg-white/5 rounded flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded bg-gradient-to-br from-[#E63946]/20 to-[#7209B7]/20 grid place-items-center flex-shrink-0">
                  <Buildings size={20} weight="duotone" className="text-[#E63946]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{p.name}</div>
                <div className="text-xs text-zinc-500">{p.founded_year || "—"}</div>
              </div>
              <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-publisher-edit-${p.publisher_id}`}><PencilSimple size={16} weight="bold" /></button>
              <button onClick={() => remove(p.publisher_id)} className="text-zinc-500 hover:text-[#E63946] p-2"><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Editora</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <PublisherFormFields form={editForm} setForm={setEditForm} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PublisherFormFields({ form, setForm }) {
  return (
    <>
      <FormField label="Nome *"><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="publisher-name-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Website (link da página)"><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." data-testid="publisher-website-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <ImageUploadField label="Imagem (logo)" value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} testid="publisher-image" />
      <FormField label="Ano de Fundação"><Input type="number" value={form.founded_year} onChange={e => setForm({...form, founded_year: e.target.value})} placeholder="1989" data-testid="publisher-year-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Descrição"><Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="publisher-description-input" className="bg-[#1C1C22] border-white/10" /></FormField>
    </>
  );
}

function MembersAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [banTarget, setBanTarget] = useState(null);
  const [duration, setDuration] = useState("1d");
  const [reason, setReason] = useState("");
  const [ipTarget, setIpTarget] = useState(null);
  const [ipData, setIpData] = useState(null);

  async function load() {
    const params = q ? { q } : {};
    const { data } = await api.get("/members", { params });
    setItems(data);
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  async function viewIps(m) {
    setIpTarget(m);
    setIpData(null);
    try {
      const { data } = await api.get(`/admin/members/${m.user_id}/ips`);
      setIpData(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }

  async function ban(e) {
    e.preventDefault();
    try {
      await api.post(`/admin/members/${banTarget.user_id}/ban`, { duration, reason });
      toast.success("Membro banido");
      setBanTarget(null); setReason(""); setDuration("1d");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function unban(id) {
    if (!confirm("Remover banimento?")) return;
    try {
      await api.post(`/admin/members/${id}/unban`);
      toast.success("Banimento removido");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  function banStatus(m) {
    if (!m.banned_until) return null;
    if (m.banned_until === "permanent") return "Permanente";
    try {
      const dt = new Date(m.banned_until);
      if (dt > new Date()) return `até ${dt.toLocaleDateString("pt-PT")} ${dt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}`;
    } catch { /* */ }
    return null;
  }

  return (
    <div className="space-y-4">
      <Input placeholder="Pesquisar por nome ou email..." value={q} onChange={(e) => setQ(e.target.value)} className="bg-[#121217] border-white/10 max-w-md" data-testid="admin-members-search" />
      <div className="space-y-2">
        {items.map(m => {
          const banned = banStatus(m);
          return (
            <div key={m.user_id} className="bg-[#121217] border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-member-${m.user_id}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-sm font-bold flex-shrink-0">
                {(m.name || "?")[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate flex items-center gap-2">
                  {m.name || "Sem nome"}
                  {m.role === "admin" && <span className="text-[10px] bg-[#E63946]/15 text-[#E63946] px-2 py-0.5 rounded font-mono-accent">ADMIN</span>}
                  {banned && <span className="text-[10px] bg-red-500/15 text-red-400 px-2 py-0.5 rounded font-mono-accent">BANIDO · {banned}</span>}
                </div>
                <div className="text-xs text-zinc-500 truncate">{m.user_id}</div>
              </div>
              {m.role !== "admin" && (
                banned ? (
                  <Button size="sm" variant="outline" className="border-white/15 text-xs" onClick={() => unban(m.user_id)} data-testid={`admin-unban-${m.user_id}`}>Remover ban</Button>
                ) : (
                  <Button size="sm" className="btn-crimson text-xs" onClick={() => setBanTarget(m)} data-testid={`admin-ban-${m.user_id}`}>
                    <ProhibitInset size={14} weight="bold" className="mr-1" /> Banir
                  </Button>
                )
              )}
              <Button size="sm" variant="outline" className="border-white/15 text-xs" onClick={() => viewIps(m)} data-testid={`admin-ip-${m.user_id}`}>
                <Globe size={14} weight="bold" className="mr-1" /> IPs
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={!!banTarget} onOpenChange={(v) => !v && setBanTarget(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Banir {banTarget?.name}</DialogTitle></DialogHeader>
          <form onSubmit={ban} className="mt-2 space-y-4">
            <FormField label="Duração">
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="bg-[#1C1C22] border-white/10" data-testid="ban-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121217] border-white/10 text-white">
                  {BAN_DURATIONS.map(d => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Motivo (opcional)">
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#1C1C22] border-white/10" data-testid="ban-reason-input" />
            </FormField>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setBanTarget(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson" data-testid="ban-confirm-btn">Confirmar Ban</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ipTarget} onOpenChange={(v) => !v && setIpTarget(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Globe size={20} weight="bold" /> IPs de {ipTarget?.name}
            </DialogTitle>
          </DialogHeader>
          {!ipData ? (
            <div className="text-center font-mono-accent text-xs text-zinc-500 py-6">A CARREGAR...</div>
          ) : (
            <div className="mt-2 space-y-3" data-testid="ip-history-list">
              {ipData.last_ip && (
                <div className="bg-[#1C1C22] border border-[#E63946]/30 rounded-lg p-3">
                  <div className="font-mono-accent text-[10px] text-[#E63946] mb-1">ÚLTIMO IP</div>
                  <div className="font-mono text-base">{ipData.last_ip}</div>
                  <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">
                    {ipData.last_ip_at && new Date(ipData.last_ip_at).toLocaleString("pt-PT")}
                  </div>
                </div>
              )}
              {ipData.ip_history.length > 1 && (
                <div>
                  <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">HISTÓRICO ({ipData.ip_history.length})</div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {ipData.ip_history.map((h, i) => (
                      <div key={i} className="flex justify-between items-center bg-[#1C1C22] rounded-md px-3 py-2 text-sm">
                        <code className="font-mono">{h.ip}</code>
                        <span className="text-[10px] text-zinc-500 font-mono-accent">
                          {h.at && new Date(h.at).toLocaleString("pt-PT")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!ipData.last_ip && (
                <div className="text-center text-sm text-zinc-500 py-6">Sem IPs registados (utilizador nunca fez login após esta funcionalidade ter sido adicionada).</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BannersAdmin() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const empty = { name: "", image_url: "", unlock_level: 1, is_exclusive: false };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [grantOpen, setGrantOpen] = useState(null); // banner being edited for exclusives
  const [memberQuery, setMemberQuery] = useState("");
  const [uploading, setUploading] = useState(false);

  async function load() {
    const { data } = await api.get("/banners");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!grantOpen) return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/members", { params: memberQuery ? { q: memberQuery, limit: 40 } : { limit: 40 } });
      setMembers(data);
    }, 250);
    return () => clearTimeout(t);
  }, [memberQuery, grantOpen]);

  async function uploadBanner(e, setter) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas imagens"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Máx 8MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload-banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.image_url);
      toast.success("Imagem carregada");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  }

  function toPayload(f) {
    return {
      name: f.name,
      image_url: f.image_url,
      unlock_level: parseInt(f.unlock_level) || 1,
      is_exclusive: !!f.is_exclusive,
    };
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.image_url) { toast.error("Carrega uma imagem para o banner"); return; }
    try {
      await api.post("/banners", toPayload(form));
      toast.success("Banner adicionado");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/banners/${editing}`, toPayload(editForm));
      toast.success("Banner atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!confirm("Apagar este banner? Utilizadores que o estejam a usar ficarão sem banner.")) return;
    await api.delete(`/banners/${id}`);
    load();
  }

  function openEdit(b) {
    setEditing(b.banner_id);
    setEditForm({
      name: b.name || "", image_url: b.image_url || "",
      unlock_level: b.unlock_level || 1, is_exclusive: !!b.is_exclusive,
    });
  }

  async function grantUser(bannerId, userId) {
    try {
      await api.post(`/banners/${bannerId}/grant/${userId}`);
      const { data } = await api.get("/banners");
      setItems(data);
      const updated = data.find(x => x.banner_id === bannerId);
      if (updated) setGrantOpen(updated);
      toast.success("Membro autorizado");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function revokeUser(bannerId, userId) {
    try {
      await api.delete(`/banners/${bannerId}/grant/${userId}`);
      const { data } = await api.get("/banners");
      setItems(data);
      const updated = data.find(x => x.banner_id === bannerId);
      if (updated) setGrantOpen(updated);
      toast.success("Acesso removido");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Novo Banner</h2>
        <BannerFormFields form={form} setForm={setForm} onUpload={(e) => uploadBanner(e, (u) => setForm({...form, image_url: u}))} uploading={uploading} />
        <Button type="submit" className="btn-crimson w-full" data-testid="banner-submit-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Adicionar Banner
        </Button>
      </form>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Banners ({items.length})</h2>
        <div className="space-y-3 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(b => (
            <div key={b.banner_id} className="bg-[#121217] border border-white/5 rounded-xl overflow-hidden" data-testid={`admin-banner-${b.banner_id}`}>
              <div className="aspect-[16/5] bg-black/50 relative">
                {b.image_url && <img src={resolveImageUrl(b.image_url)} alt="" className="w-full h-full object-cover" />}
                {b.is_exclusive && (
                  <span className="absolute top-2 left-2 bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white text-[10px] font-mono-accent font-bold px-2 py-1 rounded-full">EXCLUSIVO</span>
                )}
                <span className="absolute top-2 right-2 bg-black/60 backdrop-blur border border-white/10 text-white text-[10px] font-mono-accent font-bold px-2 py-1 rounded-full">
                  <Lock size={10} weight="bold" className="inline mr-1" /> Nv {b.unlock_level}
                </span>
              </div>
              <div className="p-3 flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold truncate">{b.name}</div>
                  <div className="text-[11px] text-zinc-500">
                    {b.is_exclusive ? `${(b.allowed_users || []).length} membros autorizados` : `Desbloqueia no nível ${b.unlock_level}`}
                  </div>
                </div>
                {b.is_exclusive && (
                  <button onClick={() => { setGrantOpen(b); setMemberQuery(""); }} className="text-zinc-300 hover:text-[#F59E0B] p-2 border border-white/10 rounded" data-testid={`admin-banner-grant-${b.banner_id}`}>
                    <UserPlus size={14} weight="bold" />
                  </button>
                )}
                <button onClick={() => openEdit(b)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-banner-edit-${b.banner_id}`}><PencilSimple size={16} weight="bold" /></button>
                <button onClick={() => remove(b.banner_id)} className="text-zinc-500 hover:text-[#E63946] p-2" data-testid={`admin-banner-delete-${b.banner_id}`}><Trash size={16} weight="bold" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 font-mono-accent text-xs">SEM BANNERS AINDA</div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Banner</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <BannerFormFields form={editForm} setForm={setEditForm} onUpload={(e) => uploadBanner(e, (u) => setEditForm({...editForm, image_url: u}))} uploading={uploading} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Grant exclusive banner to users */}
      <Dialog open={!!grantOpen} onOpenChange={(v) => !v && setGrantOpen(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Autorizar membros — {grantOpen?.name}</DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <div>
              <Label className="font-mono-accent text-[10px] text-zinc-400">MEMBROS AUTORIZADOS ({(grantOpen?.allowed_users || []).length})</Label>
              <div className="mt-2 flex flex-wrap gap-2 min-h-[40px]" data-testid="banner-allowed-users">
                {(grantOpen?.allowed_users || []).length === 0 && (
                  <div className="text-xs text-zinc-500">Ninguém ainda</div>
                )}
                {(grantOpen?.allowed_users || []).map(uid => (
                  <span key={uid} className="inline-flex items-center gap-1 bg-[#1C1C22] border border-white/10 rounded-full pl-3 pr-1 py-1 text-xs">
                    <span className="font-mono text-zinc-300">{uid}</span>
                    <button onClick={() => revokeUser(grantOpen.banner_id, uid)} className="w-5 h-5 rounded-full hover:bg-[#E63946]/20 grid place-items-center text-zinc-400 hover:text-[#E63946]" data-testid={`banner-revoke-${uid}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-mono-accent text-[10px] text-zinc-400">ADICIONAR MEMBRO</Label>
              <Input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} placeholder="Pesquisar por nome ou email..." className="mt-1 bg-[#1C1C22] border-white/10" data-testid="banner-member-search" />
              <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {members.map(m => {
                  const already = (grantOpen?.allowed_users || []).includes(m.user_id);
                  return (
                    <div key={m.user_id} className="flex items-center gap-2 bg-[#1C1C22] border border-white/5 rounded-lg px-3 py-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-xs font-bold flex-shrink-0">
                        {(m.name || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{m.name || "—"}</div>
                        <div className="text-[10px] text-zinc-500 truncate">{m.user_id}</div>
                      </div>
                      {already ? (
                        <span className="text-[10px] text-emerald-400 font-mono-accent">AUTORIZADO</span>
                      ) : (
                        <button onClick={() => grantUser(grantOpen.banner_id, m.user_id)} className="text-xs px-2 py-1 rounded btn-crimson" data-testid={`banner-grant-${m.user_id}`}>
                          <UserPlus size={12} weight="bold" className="inline mr-1" /> Autorizar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BannerFormFields({ form, setForm, onUpload, uploading }) {
  return (
    <>
      <FormField label="Nome *"><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: Naruto Sunset" data-testid="banner-name-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <div>
        <Label className="text-zinc-400 text-xs font-mono-accent">IMAGEM DO BANNER *</Label>
        <div className="mt-2 flex items-center gap-3">
          {form.image_url && (
            <img src={resolveImageUrl(form.image_url)} alt="" className="w-40 aspect-[16/5] object-cover rounded border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-sm">
            <UploadSimple size={16} weight="bold" /> {uploading ? "A carregar..." : "Carregar"}
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="banner-upload-input" />
          </label>
        </div>
        <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="ou cola um URL externo" data-testid="banner-image-input" className="mt-2 bg-[#1C1C22] border-white/10 text-xs" />
      </div>
      <FormField label="Nível necessário para desbloquear">
        <Input type="number" min="1" value={form.unlock_level} onChange={e => setForm({...form, unlock_level: e.target.value})} data-testid="banner-level-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
      <label className="flex items-center gap-3 bg-[#1C1C22] border border-white/10 rounded-lg p-3 cursor-pointer" data-testid="banner-exclusive-toggle">
        <input type="checkbox" checked={form.is_exclusive} onChange={(e) => setForm({...form, is_exclusive: e.target.checked})} className="w-4 h-4" />
        <div>
          <div className="font-display font-semibold text-sm">Banner Exclusivo</div>
          <div className="text-[11px] text-zinc-500">Ignora o nível — apenas membros que autorizares podem usar</div>
        </div>
      </label>
    </>
  );
}

function HomeBannersAdmin() {
  const [items, setItems] = useState([]);
  const empty = { image_url: "", link_url: "", order: 0 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  async function load() {
    const { data } = await api.get("/home-banners");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  async function uploadBanner(e, setter) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas imagens"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Máx 8MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload-banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.image_url);
      toast.success("Imagem carregada");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  }

  function toPayload(f) {
    return {
      image_url: f.image_url,
      link_url: f.link_url || "",
      order: parseInt(f.order) || 0,
    };
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.image_url) { toast.error("Carrega uma imagem"); return; }
    try {
      await api.post("/home-banners", toPayload(form));
      toast.success("Banner adicionado à página inicial");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.put(`/home-banners/${editing}`, toPayload(editForm));
      toast.success("Banner atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!confirm("Apagar este banner da página inicial?")) return;
    await api.delete(`/home-banners/${id}`);
    load();
  }

  function openEdit(b) {
    setEditing(b.home_banner_id);
    setEditForm({ image_url: b.image_url || "", link_url: b.link_url || "", order: b.order ?? 0 });
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Novo Banner da Home</h2>
        <div className="text-xs text-zinc-500 -mt-2 mb-1">
          Estes banners aparecem no carrossel no topo da página inicial, rodando automaticamente de 5 em 5 segundos.
        </div>
        <HomeBannerFormFields form={form} setForm={setForm} onUpload={(e) => uploadBanner(e, (u) => setForm({...form, image_url: u}))} uploading={uploading} />
        <Button type="submit" className="btn-crimson w-full" data-testid="home-banner-submit-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Adicionar
        </Button>
      </form>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Banners ({items.length})</h2>
        <div className="space-y-3 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(b => (
            <div key={b.home_banner_id} className="bg-[#121217] border border-white/5 rounded-xl overflow-hidden" data-testid={`admin-home-banner-${b.home_banner_id}`}>
              <div className="aspect-[16/5] bg-black/50 relative">
                {b.image_url && <img src={resolveImageUrl(b.image_url)} alt="" className="w-full h-full object-cover" />}
                <span className="absolute top-2 left-2 bg-black/60 backdrop-blur border border-white/10 text-white text-[10px] font-mono-accent font-bold px-2 py-1 rounded-full">ORDEM {b.order ?? 0}</span>
              </div>
              <div className="p-3 flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  {b.link_url ? (
                    <div className="text-xs text-zinc-400 truncate">→ <span className="text-[#E63946]">{b.link_url}</span></div>
                  ) : (
                    <div className="text-xs text-zinc-500">Sem link</div>
                  )}
                </div>
                <button onClick={() => openEdit(b)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-home-banner-edit-${b.home_banner_id}`}><PencilSimple size={16} weight="bold" /></button>
                <button onClick={() => remove(b.home_banner_id)} className="text-zinc-500 hover:text-[#E63946] p-2" data-testid={`admin-home-banner-delete-${b.home_banner_id}`}><Trash size={16} weight="bold" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 font-mono-accent text-xs">SEM BANNERS AINDA</div>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Banner Home</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <HomeBannerFormFields form={editForm} setForm={setEditForm} onUpload={(e) => uploadBanner(e, (u) => setEditForm({...editForm, image_url: u}))} uploading={uploading} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HomeBannerFormFields({ form, setForm, onUpload, uploading }) {
  return (
    <>
      <div>
        <Label className="text-zinc-400 text-xs font-mono-accent">IMAGEM *</Label>
        <div className="mt-2 flex items-center gap-3">
          {form.image_url && (
            <img src={resolveImageUrl(form.image_url)} alt="" className="w-40 aspect-[16/5] object-cover rounded border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-sm">
            <UploadSimple size={16} weight="bold" /> {uploading ? "A carregar..." : "Carregar"}
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="home-banner-upload-input" />
          </label>
        </div>
        <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="ou cola URL externo" data-testid="home-banner-image-input" className="mt-2 bg-[#1C1C22] border-white/10 text-xs" />
      </div>
      <FormField label="Link (opcional — para onde o clique leva)">
        <Input value={form.link_url} onChange={e => setForm({...form, link_url: e.target.value})} placeholder="/noticias/xyz  ou  https://..." data-testid="home-banner-link-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
      <FormField label="Ordem (menor aparece primeiro)">
        <Input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} data-testid="home-banner-order-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
    </>
  );
}

function GiveawaysAdmin() {
  const [items, setItems] = useState([]);
  const empty = {
    title: "", description: "", max_participants: 0, draw_date: "", draw_time: "",
    image_url: "", starts_date: "", winner_announce_date: "",
    prize: "", eligibility: "", rules_text: "",
  };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  async function load() {
    const { data } = await api.get("/giveaways");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  async function uploadImage(e, setter) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas imagens"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Máx 8MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload-banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.image_url);
      toast.success("Imagem carregada");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  }

  function toPayload(f) {
    let draw_at = "";
    if (f.draw_date && f.draw_time) {
      draw_at = new Date(`${f.draw_date}T${f.draw_time}:00`).toISOString();
    }
    return {
      title: f.title,
      description: f.description || "",
      max_participants: parseInt(f.max_participants) || 0,
      draw_at,
      image_url: f.image_url || "",
      starts_at: f.starts_date ? new Date(`${f.starts_date}T00:00:00`).toISOString() : "",
      winner_announcement_at: f.winner_announce_date ? new Date(`${f.winner_announce_date}T00:00:00`).toISOString() : "",
      prize: f.prize || "",
      eligibility: f.eligibility || "",
      rules: (f.rules_text || "").split("\n").map(s => s.trim()).filter(Boolean),
    };
  }

  async function submit(e) {
    e.preventDefault();
    const p = toPayload(form);
    if (!p.draw_at) { toast.error("Indica data e hora do sorteio"); return; }
    try {
      await api.post("/giveaways", p);
      toast.success("Sorteio criado");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function saveEdit(e) {
    e.preventDefault();
    const p = toPayload(editForm);
    if (!p.draw_at) { toast.error("Indica data e hora"); return; }
    try {
      await api.put(`/giveaways/${editing}`, p);
      toast.success("Sorteio atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!confirm("Apagar este sorteio? Todos os participantes serão removidos.")) return;
    await api.delete(`/giveaways/${id}`);
    load();
  }

  function openEdit(g) {
    const pad = (n) => String(n).padStart(2, "0");
    const dateOnly = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    const d = new Date(g.draw_at);
    setEditing(g.giveaway_id);
    setEditForm({
      title: g.title || "", description: g.description || "",
      max_participants: g.max_participants || 0,
      draw_date: dateOnly(g.draw_at),
      draw_time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      image_url: g.image_url || "",
      starts_date: dateOnly(g.starts_at),
      winner_announce_date: dateOnly(g.winner_announcement_at),
      prize: g.prize || "",
      eligibility: g.eligibility || "",
      rules_text: (g.rules || []).join("\n"),
    });
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} className="bg-[#121217] border border-white/5 rounded-2xl p-6 space-y-3">
        <h2 className="font-display text-xl font-bold mb-3">Novo Sorteio</h2>
        <div className="text-xs text-zinc-500 -mt-2 mb-1">
          Na data e hora definidas, o sistema escolhe automaticamente um vencedor aleatório. Podes aprovar ou recusar; ao recusar tens de indicar motivo.
        </div>
        <GiveawayFormFields form={form} setForm={setForm} onUpload={(e) => uploadImage(e, (u) => setForm({...form, image_url: u}))} uploading={uploading} />
        <Button type="submit" className="btn-crimson w-full" data-testid="giveaway-submit-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Criar Sorteio
        </Button>
      </form>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Sorteios ({items.length})</h2>
        <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map(g => {
            const dt = new Date(g.draw_at);
            return (
              <div key={g.giveaway_id} className="bg-[#121217] border border-white/5 rounded-xl p-3" data-testid={`admin-giveaway-${g.giveaway_id}`}>
                <div className="flex items-start gap-3">
                  {g.image_url ? (
                    <img src={resolveImageUrl(g.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#E63946]/20 to-[#7209B7]/20 grid place-items-center flex-shrink-0">
                      <Gift size={22} weight="fill" className="text-[#E63946]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold truncate">#{g.sequence_number || "—"} · {g.title}</div>
                    <div className="text-[11px] text-zinc-500 font-mono-accent">
                      {dt.toLocaleDateString("pt-PT")} · {g.participant_count} inscritos · {g.status.toUpperCase()}
                    </div>
                  </div>
                  <a href={`/sorteio/${g.giveaway_id}`} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-[#F59E0B] p-2" title="Ver">
                    <Gift size={14} weight="bold" />
                  </a>
                  <button onClick={() => openEdit(g)} className="text-zinc-400 hover:text-[#7209B7] p-2" data-testid={`admin-giveaway-edit-${g.giveaway_id}`}><PencilSimple size={14} weight="bold" /></button>
                  <button onClick={() => remove(g.giveaway_id)} className="text-zinc-500 hover:text-[#E63946] p-2" data-testid={`admin-giveaway-delete-${g.giveaway_id}`}><Trash size={14} weight="bold" /></button>
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 font-mono-accent text-xs">SEM SORTEIOS AINDA</div>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Editar Sorteio</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-3">
            <GiveawayFormFields form={editForm} setForm={setEditForm} onUpload={(e) => uploadImage(e, (u) => setEditForm({...editForm, image_url: u}))} uploading={uploading} />
            <div className="flex gap-2 mt-4">
              <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-crimson">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GiveawayFormFields({ form, setForm, onUpload, uploading }) {
  return (
    <>
      <FormField label="Título *"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Ex: Sorteio de Manga" data-testid="giveaway-title-input" className="bg-[#1C1C22] border-white/10" /></FormField>
      <FormField label="Prémio (nome curto que aparece no card lateral)">
        <Input value={form.prize} onChange={e => setForm({...form, prize: e.target.value})} placeholder="Ex: Coleção Chainsaw Man Volumes 1-3" data-testid="giveaway-prize-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
      <div>
        <Label className="text-zinc-400 text-xs font-mono-accent">IMAGEM DO PRÉMIO</Label>
        <div className="mt-2 flex items-center gap-3">
          {form.image_url && (
            <img src={resolveImageUrl(form.image_url)} alt="" className="w-24 h-24 object-cover rounded border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-sm">
            <UploadSimple size={16} weight="bold" /> {uploading ? "A carregar..." : "Carregar"}
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="giveaway-image-upload" />
          </label>
        </div>
        <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="ou URL externo" data-testid="giveaway-image-input" className="mt-2 bg-[#1C1C22] border-white/10 text-xs" />
      </div>
      <FormField label="Descrição">
        <Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Descreve o prémio e a promoção..." data-testid="giveaway-description-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Data de início">
          <Input type="date" value={form.starts_date} onChange={e => setForm({...form, starts_date: e.target.value})} data-testid="giveaway-starts-input" className="bg-[#1C1C22] border-white/10" />
        </FormField>
        <FormField label="Anúncio do vencedor">
          <Input type="date" value={form.winner_announce_date} onChange={e => setForm({...form, winner_announce_date: e.target.value})} data-testid="giveaway-announce-input" className="bg-[#1C1C22] border-white/10" />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Data do sorteio *">
          <Input type="date" required value={form.draw_date} onChange={e => setForm({...form, draw_date: e.target.value})} data-testid="giveaway-date-input" className="bg-[#1C1C22] border-white/10" />
        </FormField>
        <FormField label="Hora do sorteio *">
          <Input type="time" required value={form.draw_time} onChange={e => setForm({...form, draw_time: e.target.value})} data-testid="giveaway-time-input" className="bg-[#1C1C22] border-white/10" />
        </FormField>
      </div>
      <FormField label="Limite de membros (0 = sem limite)">
        <Input type="number" min="0" value={form.max_participants} onChange={e => setForm({...form, max_participants: e.target.value})} data-testid="giveaway-max-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
      <FormField label="Elegibilidade">
        <Input value={form.eligibility} onChange={e => setForm({...form, eligibility: e.target.value})} placeholder="Ex: Apenas para residentes em Portugal" data-testid="giveaway-eligibility-input" className="bg-[#1C1C22] border-white/10" />
      </FormField>
      <FormField label="Regras (uma por linha)">
        <Textarea rows={5} value={form.rules_text} onChange={e => setForm({...form, rules_text: e.target.value})} placeholder="Apenas uma participação por pessoa.&#10;Conta e email verificados são obrigatórios.&#10;O vencedor será selecionado aleatoriamente.&#10;O vencedor terá 7 dias para responder." data-testid="giveaway-rules-input" className="bg-[#1C1C22] border-white/10 font-mono text-sm" />
      </FormField>
    </>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs font-mono-accent">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
